class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.renderToken = 0;

    // A page restored from the back/forward cache still shows the cart as it was when the
    // shopper left it. Mark it stale and re-fetch, so the drawer never opens out of date.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      this.stale = true;
      this.refresh().catch((e) => console.error(e));
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.stale) this.refresh().catch((e) => console.error(e));
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /*
   * Renders the drawer from a /cart/add (or /cart/change) response that carries `sections`.
   * Shopify can answer a successful add without usable section HTML (sections: null, or a
   * section that failed to render). The old code then threw half way, left the page-load
   * "Your cart is empty" markup in place and stripped .is-empty, so the shopper later saw an
   * empty drawer although the item was in the cart. Now we fall back to fetching fresh
   * sections, and only open the drawer once it shows the real cart.
   * Returns a promise that rejects if the drawer could not be brought up to date.
   */
  renderContents(parsedState) {
    this.productId = parsedState && parsedState.id;
    const sections = parsedState && parsedState.sections;
    const usable = sections && this.getSectionsToRender().every((section) => {
      return typeof sections[section.id] === 'string' && this.getSectionDOM(sections[section.id], section.selector);
    });
    const ready = usable ? Promise.resolve(sections) : this.fetchSections();
    const token = ++this.renderToken;

    return ready.then((fresh) => {
      if (token !== this.renderToken) return; // a newer render already ran
      this.applySections(fresh);
      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.open();
      });
    });
  }

  // Fresh drawer + bubble HTML straight from the server (always reflects the current cart).
  fetchSections() {
    const ids = this.getSectionsToRender().map((section) => section.id).join(',');
    // No Accept: application/json header: on a product URL that returns the product JSON instead.
    return fetch(`${routes.cart_url}?sections=${ids}`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart sections request failed (${response.status})`);
        return response.json();
      })
      .then((sections) => {
        if (!sections || typeof sections['cart-drawer'] !== 'string') throw new Error('Cart sections missing');
        return sections;
      });
  }

  applySections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const source = this.getSectionDOM(sections[section.id], section.selector);
      if (sectionElement && source) sectionElement.innerHTML = source.innerHTML;
    });
    this.syncEmptyState();
    this.stale = false;
  }

  // The empty layout is driven by .is-empty on <cart-drawer>; keep it in step with the markup.
  syncEmptyState() {
    this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
  }

  // Re-render without opening (back/forward cache restores, cart changed elsewhere).
  refresh() {
    const token = ++this.renderToken;
    return this.fetchSections().then((sections) => {
      if (token === this.renderToken) this.applySections(sections);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
